import type {
  EnvironmentId,
  ClientOrchestrationShellSnapshot,
  ClientOrchestrationShellStreamEvent,
  ServerConfig,
  ServerLifecycleWelcomePayload,
} from "@t3tools/contracts";
import {
  type WsRpcClient,
  getReconnectDelayMs,
  DEFAULT_RECONNECT_BACKOFF,
} from "@t3tools/client-runtime";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";

import type { PrimaryKnownEnvironment } from "../primary/target";

export interface EnvironmentConnection {
  readonly environmentId: EnvironmentId;
  readonly knownEnvironment: PrimaryKnownEnvironment;
  readonly client: WsRpcClient;
  readonly ensureBootstrapped: () => Promise<void>;
  readonly reconnect: () => Promise<void>;
  readonly dispose: () => Promise<void>;
}

interface EnvironmentConnectionInput {
  readonly fetchShellSnapshot: () => Promise<ClientOrchestrationShellSnapshot>;
  readonly knownEnvironment: PrimaryKnownEnvironment;
  readonly client: WsRpcClient;
  readonly applyShellEvent: (
    event: ClientOrchestrationShellStreamEvent,
    environmentId: EnvironmentId,
  ) => void;
  readonly syncShellSnapshot: (
    snapshot: ClientOrchestrationShellSnapshot,
    environmentId: EnvironmentId,
  ) => void;
  readonly onConfigSnapshot?: (config: ServerConfig) => void;
  readonly onWelcome?: (payload: ServerLifecycleWelcomePayload) => void;
  readonly onShellResubscribe?: (environmentId: EnvironmentId) => void;
}

export class EnvironmentConnectionDisposedError extends Error {
  constructor(environmentId: EnvironmentId) {
    super(`Environment connection ${environmentId} was disposed before it finished bootstrapping.`);
    this.name = "EnvironmentConnectionDisposedError";
  }
}

function createBootstrapGate() {
  let resolve: (() => void) | null = null;
  let reject: ((error: unknown) => void) | null = null;
  const makePromise = () => {
    const nextPromise = new Promise<void>((nextResolve, nextReject) => {
      resolve = nextResolve;
      reject = nextReject;
    });
    void nextPromise.catch(() => undefined);
    return nextPromise;
  };
  let promise = makePromise();

  return {
    wait: () => promise,
    resolve: () => {
      resolve?.();
      resolve = null;
      reject = null;
    },
    reject: (error: unknown) => {
      reject?.(error);
      resolve = null;
      reject = null;
    },
    reset: () => {
      promise = makePromise();
    },
  };
}

export function createEnvironmentConnection(
  input: EnvironmentConnectionInput,
): EnvironmentConnection {
  const environmentId = input.knownEnvironment.environmentId;
  let disposed = false;
  const bootstrapGate = createBootstrapGate();

  const observeEnvironmentIdentity = (nextEnvironmentId: EnvironmentId, source: string) => {
    if (environmentId !== nextEnvironmentId) {
      throw new Error(
        `Environment connection ${environmentId} changed identity to ${nextEnvironmentId} via ${source}.`,
      );
    }
  };

  const unsubscribeLifecycle = input.client.server.subscribeLifecycle((event) => {
    if (disposed || event.type !== "welcome") return;
    observeEnvironmentIdentity(event.payload.environment.environmentId, "server lifecycle welcome");
    input.onWelcome?.(event.payload);
  });

  const unsubscribeConfig = input.client.server.subscribeConfig((event) => {
    if (disposed || event.type !== "snapshot") return;
    observeEnvironmentIdentity(event.config.environment.environmentId, "server config snapshot");
    input.onConfigSnapshot?.(event.config);
  });

  let lastSequence: number | undefined;
  let unsubscribeShell: () => void = () => undefined;
  let snapshotRequest: Promise<void> | null = null;
  let shellGeneration = 0;
  let snapshotRetry = 0;
  let cancelSnapshotRetry: () => void = () => undefined;

  const loadSnapshot = (): Promise<void> => {
    if (snapshotRequest) return snapshotRequest;
    cancelSnapshotRetry();
    const generation = ++shellGeneration;
    unsubscribeShell();
    lastSequence = undefined;
    bootstrapGate.reset();
    snapshotRequest = input
      .fetchShellSnapshot()
      .then((snapshot) => {
        snapshotRequest = null;
        if (disposed) return;
        snapshotRetry = 0;
        input.syncShellSnapshot(snapshot, environmentId);
        lastSequence = snapshot.snapshotSequence;
        unsubscribeShell = input.client.orchestration.subscribeShell(
          (item) => {
            if (disposed || generation !== shellGeneration) return;
            if (item.kind === "resync") {
              shellGeneration++;
              unsubscribeShell();
              queueMicrotask(() => {
                if (!disposed) void loadSnapshot().catch(() => undefined);
              });
              return;
            }
            if (item.kind === "snapshot") {
              input.syncShellSnapshot(item.snapshot, environmentId);
              lastSequence = item.snapshot.snapshotSequence;
              return;
            }
            if (item.sequence <= lastSequence!) return;
            input.applyShellEvent(item, environmentId);
            lastSequence = item.sequence;
          },
          {
            afterSequence: () => lastSequence!,
            onResubscribe: () => {
              if (!disposed) input.onShellResubscribe?.(environmentId);
            },
          },
        );
        bootstrapGate.resolve();
      })
      .catch((error: unknown) => {
        snapshotRequest = null;
        bootstrapGate.reject(error);
        if (!disposed) {
          const delay = getReconnectDelayMs(snapshotRetry++, {
            ...DEFAULT_RECONNECT_BACKOFF,
            maxRetries: null,
          });
          cancelSnapshotRetry = Effect.runCallback(Effect.sleep(delay!), {
            onExit: (exit) => {
              if (Exit.isSuccess(exit) && !disposed) void loadSnapshot().catch(() => undefined);
            },
          });
        }
        throw error;
      });
    return snapshotRequest;
  };

  void loadSnapshot().catch(() => undefined);

  return {
    environmentId,
    knownEnvironment: input.knownEnvironment,
    client: input.client,
    ensureBootstrapped: () =>
      disposed
        ? Promise.reject(new EnvironmentConnectionDisposedError(environmentId))
        : bootstrapGate.wait(),
    reconnect: async () => {
      if (disposed) throw new EnvironmentConnectionDisposedError(environmentId);
      try {
        await input.client.reconnect();
        if (lastSequence === undefined) await loadSnapshot();
        await bootstrapGate.wait();
      } catch (error) {
        bootstrapGate.reject(error);
        throw error;
      }
    },
    dispose: async () => {
      if (disposed) return;
      disposed = true;
      cancelSnapshotRetry();
      bootstrapGate.reject(new EnvironmentConnectionDisposedError(environmentId));
      unsubscribeShell();
      unsubscribeLifecycle();
      unsubscribeConfig();
      await input.client.dispose();
    },
  };
}
